import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';

import * as tenantsApi from '../../api/tenants';
import type { BillingMode, Tenant, TenantContactPatch } from '../../api/tenants';
import {
  filterPhoneInput,
  PHONE_LIKE_FIELDS,
  tenantProfileValidators,
  validateEmail,
  type TenantProfileField,
} from '../../validation/profile';
import {
  asCountryCode,
  formatPhoneAsYouType,
  getCountryCallingCodePrefix,
  getPhonePlaceholder,
  validatePhone,
} from '../../validation/phone';
import { findCountry, SUPPORTED_COUNTRIES } from '../../lookup/countries';
import { usePostalLookup } from '../../lookup/usePostalLookup';
import { slugifyName } from '../../util/slug';
import { SearchableSelect } from '../../components/SearchableSelect';

type TrialDays = '' | '7' | '14' | '21';

const BILLING_OPTIONS: { value: BillingMode; label: string }[] = [
  { value: 'tenant_pays', label: 'Tenant pays (one subscription)' },
  { value: 'participant_pays', label: 'Each participant pays' },
];

// Trial lengths keep their numeric order (keepOrder); "None" is pinned on
// top. A trial here is a trial license (TENANT-LICENSES).
const TRIAL_OPTIONS: { value: TrialDays; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '21', label: '21 days' },
];
const TRIAL_PINNED: { value: TrialDays; label: string }[] = [
  { value: '', label: 'None — issue a license later' },
];

// Country suggestions, alphabetical by display name.
const COUNTRY_NAMES = SUPPORTED_COUNTRIES.map((c) => c.name).sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' }),
);

/**
 * Create / edit a tenant in a single dialog. In `create` mode an
 * optional "initial admin email" field invites the first tenant_admin
 * in the same request — handy because typing slug + name + email in
 * one form is shorter than slug + name → save → click Invite.
 *
 * Slug is immutable in edit mode (changing it would mean renaming
 * a Postgres schema, which is a separate operation entirely).
 *
 * Validation: phone/fax/website/contact_email/city/state/postal/country
 * run regex checks on blur and again on submit. The Save button is
 * disabled while any error is present. The server-side schemas
 * (api/app/schemas/validation.py) enforce the same regexes — client
 * validation is a UX-only layer.
 */

type ContactForm = {
  contact_name: string;
  contact_email: string;
  phone: string;
  fax: string;
  website: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state_region: string;
  postal_code: string;
  country: string;
};

const emptyContact: ContactForm = {
  contact_name: '',
  contact_email: '',
  phone: '',
  fax: '',
  website: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state_region: '',
  postal_code: '',
  country: '',
};

function tenantToContact(t: Tenant | undefined): ContactForm {
  if (!t) return emptyContact;
  return {
    contact_name: t.contact_name ?? '',
    contact_email: t.contact_email ?? '',
    phone: t.phone ?? '',
    fax: t.fax ?? '',
    website: t.website ?? '',
    address_line1: t.address_line1 ?? '',
    address_line2: t.address_line2 ?? '',
    city: t.city ?? '',
    state_region: t.state_region ?? '',
    postal_code: t.postal_code ?? '',
    country: t.country ?? '',
  };
}

type TenantErrorMap = Partial<Record<TenantProfileField, string>> & {
  initial_admin_email?: string;
};

export function TenantFormDialog({
  mode,
  tenant,
  onClose,
  onSaved,
  restrictToContactFields = false,
}: {
  mode: 'create' | 'edit';
  tenant?: Tenant;
  onClose(): void;
  onSaved(): void;
  /**
   * When true, hides fields the tenant_admin role can't change:
   * Slug (already immutable in edit mode), Billing mode, and the
   * Initial admin email (create-only). Used by the tenant-admin
   * /tenant/settings page; the backend will 403 these fields for
   * non-superadmin callers anyway, but hiding them in the UI keeps
   * the form honest about what the user can do.
   */
  restrictToContactFields?: boolean;
}) {
  const isEdit = mode === 'edit' && tenant !== undefined;
  const existingAdminEmails = tenant?.tenant_admin_emails ?? [];
  // A tenant can be created without a tenant_admin; the edit dialog is the
  // superadmin's recovery path to invite the *first* one. Once any active
  // admin exists, this drops back to the read-only mirror and further admins
  // are managed from the tenant's Admins page. Hidden from tenant_admins
  // (restrictToContactFields) — they invite from their own Settings page.
  const canInviteFirstAdmin =
    isEdit && !restrictToContactFields && existingAdminEmails.length === 0;
  const [slug, setSlug] = useState(tenant?.slug ?? '');
  const [name, setName] = useState(tenant?.name ?? '');
  const [billingMode, setBillingMode] = useState<BillingMode>(
    tenant?.billing_mode ?? 'tenant_pays',
  );
  const [initialAdminEmail, setInitialAdminEmail] = useState('');
  // Email-bypass enrollment mode. Superadmin-only — hidden when the form
  // is restricted to the tenant_admin's contact-only fields.
  const [directEntry, setDirectEntry] = useState(
    tenant?.direct_participant_entry ?? false,
  );
  // Optional trial period for new tenants only. '' = no trial.
  // Restricted to the platform-blessed values (matches backend).
  const [trialPeriodDays, setTrialPeriodDays] = useState<TrialDays>('');
  // Once the user manually edits the slug field, stop auto-syncing
  // it from name — the classic "dirty bit" pattern. In edit mode the
  // slug is immutable anyway, so start dirty.
  const [slugDirty, setSlugDirty] = useState(isEdit);
  const [contact, setContact] = useState<ContactForm>(tenantToContact(tenant));
  const [errors, setErrors] = useState<TenantErrorMap>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const base = tenantToContact(tenant);
  const hasErrors = Object.values(errors).some(Boolean);

  // Postal-code lookup is authoritative: a fresh Zippopotam hit
  // overwrites city + state so misspellings get corrected when the
  // user enters a valid ZIP.
  const country = findCountry(contact.country);
  const countryCode = asCountryCode(country?.code);
  const lookup = usePostalLookup(country?.code ?? null, contact.postal_code);
  useEffect(() => {
    if (!lookup.result) return;
    setContact((c) => ({
      ...c,
      city: lookup.result!.city,
      state_region: lookup.result!.state,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.city;
      delete next.state_region;
      return next;
    });
  }, [lookup.result]);

  // When country changes, re-format and re-validate any phone/fax
  // fields already filled in. US digit-counts differ from UK etc.
  useEffect(() => {
    if (!countryCode) return;
    setContact((c) => ({
      ...c,
      phone: c.phone ? formatPhoneAsYouType(c.phone, countryCode) : c.phone,
      fax: c.fax ? formatPhoneAsYouType(c.fax, countryCode) : c.fax,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const field of ['phone', 'fax'] as const) {
        const msg = validatePhone(contact[field], countryCode);
        if (msg) next[field] = msg;
        else delete next[field];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  const phoneHelperFor = (field: 'phone' | 'fax'): string => {
    if (errors[field]) return errors[field]!;
    if (countryCode) {
      const prefix = getCountryCallingCodePrefix(countryCode);
      return prefix ? `${countryCode} format · prefix ${prefix}` : ' ';
    }
    return ' ';
  };

  const update = (k: keyof ContactForm) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;

    // Phone-like fields: filter to allowed chars, then format via
    // libphonenumber's AsYouType against the selected country.
    let value = raw;
    if (PHONE_LIKE_FIELDS.has(k)) {
      const filtered = filterPhoneInput(raw);
      value = countryCode
        ? formatPhoneAsYouType(filtered, countryCode)
        : filtered;
    }

    setContact((c) => ({ ...c, [k]: value }));

    if (PHONE_LIKE_FIELDS.has(k)) {
      const field = k as TenantProfileField;
      const msg = validatePhone(value, countryCode);
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) next[field] = msg;
        else delete next[field];
        return next;
      });
      return;
    }

    if (k in tenantProfileValidators) {
      const field = k as TenantProfileField;
      const msg = tenantProfileValidators[field](value);
      setErrors((prev) => {
        const next = { ...prev };
        if (msg) next[field] = msg;
        else delete next[field];
        return next;
      });
    }
  };

  const errorProps = (k: TenantProfileField) => ({
    error: Boolean(errors[k]),
    helperText: errors[k] ?? ' ',
  });

  const changedContact = (): TenantContactPatch => {
    const out: TenantContactPatch = {};
    (Object.keys(contact) as (keyof ContactForm)[]).forEach((k) => {
      if (isEdit) {
        if (contact[k] !== base[k]) out[k] = contact[k];
      } else if (contact[k] !== '') {
        out[k] = contact[k];
      }
    });
    return out;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Re-validate every formatted field at submit time.
    const newErrors: TenantErrorMap = {};
    (Object.keys(tenantProfileValidators) as TenantProfileField[]).forEach((k) => {
      const m = tenantProfileValidators[k](contact[k]);
      if (m) newErrors[k] = m;
    });
    if (!isEdit || canInviteFirstAdmin) {
      // Optional in both modes (blank = skip); validateEmail passes on ''.
      const adminError = validateEmail(initialAdminEmail);
      if (adminError) newErrors.initial_admin_email = adminError;
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      if (isEdit) {
        // tenant_admin can't change billing_mode (backend 403); the
        // field is hidden in that case via restrictToContactFields,
        // and the body must skip it so the server doesn't read the
        // unchanged value as an attempted change.
        await tenantsApi.updateTenant(tenant.id, {
          name,
          ...(restrictToContactFields
            ? {}
            : {
                billing_mode: billingMode,
                direct_participant_entry: directEntry,
              }),
          // First-admin recovery: only when the tenant has none and the
          // operator actually filled the field.
          ...(canInviteFirstAdmin && initialAdminEmail
            ? { initial_admin_email: initialAdminEmail }
            : {}),
          ...changedContact(),
        });
      } else {
        await tenantsApi.createTenant({
          slug,
          name,
          billing_mode: billingMode,
          direct_participant_entry: directEntry,
          initial_admin_email: initialAdminEmail || undefined,
          trial_period_days: trialPeriodDays
            ? (Number(trialPeriodDays) as 7 | 14 | 21)
            : undefined,
          ...changedContact(),
        });
      }
      onSaved();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'save failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? `Edit ${tenant.name}` : 'New tenant'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Name"
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setName(v);
                    // Auto-derive the slug while the user hasn't
                    // touched the slug field directly. Once they
                    // type in slug we stop tracking name.
                    if (!slugDirty) {
                      setSlug(slugifyName(v));
                    }
                  }}
                  disabled={submitting}
                  helperText={name.trim() === '' ? 'Name is required.' : ' '}
                  error={name.trim() === ''}
                />
              </Grid>
              {!restrictToContactFields && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Slug"
                    required
                    fullWidth
                    value={slug}
                    onChange={(e) => {
                      setSlug(e.target.value);
                      setSlugDirty(true);
                    }}
                    disabled={isEdit || submitting}
                    helperText={
                      isEdit
                        ? 'Slug is immutable after creation.'
                        : slugDirty
                        ? 'lowercase letters, digits, underscore'
                        : 'auto-generated from name — edit to override'
                    }
                    inputProps={{ pattern: '^[a-z][a-z0-9_]{1,46}$' }}
                  />
                </Grid>
              )}
              {!restrictToContactFields && (
                <Grid size={{ xs: 12, sm: isEdit ? 12 : 6 }}>
                  <SearchableSelect<BillingMode>
                    id="tenant-billing-mode"
                    label="Billing mode"
                    size="medium"
                    fullWidth
                    value={billingMode}
                    onChange={setBillingMode}
                    options={BILLING_OPTIONS}
                    disabled={submitting}
                    helperText="Who pays for the platform: the tenant as a whole, or each participant individually."
                  />
                </Grid>
              )}
              {!restrictToContactFields && !isEdit && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <SearchableSelect<TrialDays>
                    id="tenant-trial-period"
                    label="Trial license"
                    size="medium"
                    fullWidth
                    value={trialPeriodDays}
                    onChange={setTrialPeriodDays}
                    options={TRIAL_OPTIONS}
                    pinned={TRIAL_PINNED}
                    keepOrder
                    disabled={submitting}
                    helperText="Optional. Issues a trial license; when it ends the tenant becomes read-only. Renew it on the Licenses page."
                  />
                </Grid>
              )}
            </Grid>

            {!restrictToContactFields && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={directEntry}
                      onChange={(e) => setDirectEntry(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="Direct participant entry (skip email invitations)"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ ml: 4 }}
                >
                  For live demos/sessions: the tenant admin adds
                  participants from a form (email optional) instead of
                  sending email invitations.
                </Typography>
              </Box>
            )}

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Contact
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Contact name"
                  fullWidth
                  value={contact.contact_name}
                  onChange={update('contact_name')}
                  disabled={submitting}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Contact email"
                  type="email"
                  fullWidth
                  value={contact.contact_email}
                  onChange={update('contact_email')}
                  disabled={submitting}
                  {...errorProps('contact_email')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={contact.phone}
                  onChange={update('phone')}
                  disabled={submitting}
                  error={Boolean(errors.phone)}
                  helperText={phoneHelperFor('phone')}
                  placeholder={getPhonePlaceholder(countryCode)}
                  inputProps={{ inputMode: 'tel' }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Fax"
                  fullWidth
                  value={contact.fax}
                  onChange={update('fax')}
                  disabled={submitting}
                  error={Boolean(errors.fax)}
                  helperText={phoneHelperFor('fax')}
                  placeholder={getPhonePlaceholder(countryCode)}
                  inputProps={{ inputMode: 'tel' }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Website"
                  fullWidth
                  value={contact.website}
                  onChange={update('website')}
                  disabled={submitting}
                  placeholder="https://"
                  {...errorProps('website')}
                />
              </Grid>
            </Grid>

            <Divider sx={{ pt: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Mailing address
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Address line 1"
                  fullWidth
                  value={contact.address_line1}
                  onChange={update('address_line1')}
                  disabled={submitting}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Address line 2"
                  fullWidth
                  value={contact.address_line2}
                  onChange={update('address_line2')}
                  disabled={submitting}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="City"
                  fullWidth
                  value={contact.city}
                  onChange={update('city')}
                  disabled={submitting}
                  {...errorProps('city')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="State / region"
                  fullWidth
                  value={contact.state_region}
                  onChange={update('state_region')}
                  disabled={submitting}
                  {...errorProps('state_region')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Postal code"
                  fullWidth
                  value={contact.postal_code}
                  onChange={update('postal_code')}
                  disabled={submitting}
                  {...errorProps('postal_code')}
                  InputProps={{
                    endAdornment: lookup.loading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : lookup.result ? (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          ✓ {lookup.result.city}, {lookup.result.stateAbbr || lookup.result.state}
                        </Typography>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  freeSolo
                  options={COUNTRY_NAMES}
                  value={contact.country}
                  onChange={(_, v) => {
                    const value = v ?? '';
                    setContact((c) => ({ ...c, country: value }));
                    const msg = tenantProfileValidators.country(value);
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (msg) next.country = msg;
                      else delete next.country;
                      return next;
                    });
                  }}
                  onInputChange={(_, v) => {
                    setContact((c) => ({ ...c, country: v }));
                    const msg = tenantProfileValidators.country(v);
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (msg) next.country = msg;
                      else delete next.country;
                      return next;
                    });
                  }}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      {...errorProps('country')}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {(!isEdit || canInviteFirstAdmin) && (
              <Box>
                <Divider sx={{ pt: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    {isEdit
                      ? 'Invite the first tenant admin'
                      : 'Optional: invite the first tenant admin'}
                  </Typography>
                </Divider>
                <TextField
                  label="Tenant admin email"
                  type="email"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={initialAdminEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInitialAdminEmail(value);
                    // Validate on change so an invalid email errors
                    // immediately, matching the rest of the form.
                    const m = validateEmail(value);
                    setErrors((p) => ({ ...p, initial_admin_email: m ?? undefined }));
                  }}
                  disabled={submitting}
                  error={Boolean(errors.initial_admin_email)}
                  helperText={
                    errors.initial_admin_email ??
                    (isEdit
                      ? "This tenant has no admin yet. Enter an email to invite its first tenant admin (or leave blank and save). If this email already has a 10NEWTONS account, accepting the invitation adds the tenant_admin role to it — it doesn't create a new one."
                      : "Leave blank to skip; you can invite admins later from the tenant page. If this email already has a 10NEWTONS account, accepting the invitation adds the tenant_admin role to that account — it doesn't create a new one.")
                  }
                />
              </Box>
            )}

            {isEdit && !canInviteFirstAdmin && (
              // Read-only mirror of the current tenant_admin email(s).
              // Editing the assignment lives on the tenant detail page
              // (and ultimately on a per-membership management surface);
              // surfacing it here is just informational so the operator
              // knows who they're editing the tenant for. Populated by
              // GET /tenants/{id}; comma-joined for compactness on the
              // typical "one tenant_admin per tenant" case.
              <Box>
                <Divider sx={{ pt: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Tenant admin
                  </Typography>
                </Divider>
                <TextField
                  label="Tenant admin email"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={existingAdminEmails.join(', ')}
                  InputProps={{ readOnly: true }}
                  helperText={
                    existingAdminEmails.length === 0
                      ? 'No active tenant admin yet.'
                      : 'Read-only here. Manage tenant_admin memberships from the Admins page.'
                  }
                />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting || hasErrors}>
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
